import React, { useState, useEffect } from "react";
import { doc, onSnapshot, query, collection, addDoc, setDoc, Timestamp, orderBy, limit } from "../../config/dataClient";
import { Clock, Navigation, ShieldAlert, MapPin, LogIn } from "lucide-react";
import WorkSpinner from "../ui/WorkSpinner";
import PaperCharm from "../ui/PaperCharm";

import { db } from "../../config/firebase";
import { isLocalPreview } from "../../config/previewMode";
import { APP_ID } from "../../config/constants";
import { formatTime, formatDate } from "../../utils/timeHelpers";
import { getDistanceFromLatLonInMeters } from "../../utils/locationHelpers";

export default function HomeView({ user, userProfile }) {
  const [logs, setLogs] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentIP, setCurrentIP] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [companySettings, setCompanySettings] = useState({ allowedIP: "", companyLat: null, companyLng: null });
  const [gpsError, setGpsError] = useState(null);
  const [distance, setDistance] = useState(null);
  const [lastStatusLog, setLastStatusLog] = useState(null);

  // 1. 計時器：只負責更新畫面上的時間顯示
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. 取得 IP 與 公司設定
  useEffect(() => {
    if (!user) return;
    if (isLocalPreview) setCurrentIP("127.0.0.1");
    else fetch("https://api.ipify.org?format=json").then((r) => r.json()).then((d) => setCurrentIP(d.ip)).catch(console.error);
    const unsub = onSnapshot(doc(db, "artifacts", APP_ID, "public", "data", "settings", "main"), (doc) => {
      if (doc.exists()) setCompanySettings(doc.data());
    });
    return () => unsub();
  }, [user]);

  // 3. GPS 定位
  useEffect(() => {
    if (isLocalPreview) return;
    if (!navigator.geolocation) { setGpsError("瀏覽器不支援定位"); return; }
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGpsError(null);
      },
      (error) => { console.warn("GPS Watch Error:", error.message); setGpsError("定位失敗"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // 4. 計算距離
  useEffect(() => {
    if (currentLocation && companySettings.companyLat) {
      const dist = getDistanceFromLatLonInMeters(currentLocation.lat, currentLocation.lng, companySettings.companyLat, companySettings.companyLng);
      setDistance(Math.round(dist));
    } else {
      setDistance(null);
    }
  }, [currentLocation, companySettings]);

  // 5. 監聽打卡紀錄
  useEffect(() => {
    if (!user) return;
    
    const q = query(collection(db, "artifacts", APP_ID, "users", user.uid, "logs"));
    
    const unsub = onSnapshot(q, (snapshot) => {
      const allLogs = snapshot.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data(), 
          dateObj: doc.data().timestamp?.toDate() || new Date() 
      }));
      
      // 前端排序：最新的在前
      allLogs.sort((a, b) => b.dateObj - a.dateObj);
      
      // 更新最後狀態
      if (allLogs.length > 0) setLastStatusLog(allLogs[0]);
      else setLastStatusLog(null);

      // 這裡計算「當日」顯示列表
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const nextDay = new Date(now);
      nextDay.setDate(nextDay.getDate() + 1);
      
      setLogs(allLogs.filter((log) => log.dateObj >= now && log.dateObj < nextDay));
    });

    return () => unsub();
  }, [user]);

  const isClockedIn = lastStatusLog?.type === "in";
  const nextAction = isClockedIn ? "out" : "in";

  const isIpValid = !companySettings.allowedIP || companySettings.allowedIP === currentIP;
  const isGpsValid = !companySettings.companyLat || (distance !== null && distance <= 300);
  
  // 先判斷「位置/IP 是否合格」
  let isLocationValid = true;
  let statusMessage = "準備就緒";

  if (companySettings.allowedIP && companySettings.companyLat) {
    if (!isIpValid && !isGpsValid) { isLocationValid = false; statusMessage = "未連接公司網路 且 距離公司太遠"; }
    else if (isIpValid) { statusMessage = "已連接公司網路 (GPS略過)"; }
    else { statusMessage = `定位有效 (距離 ${distance}m)`; }
  } else if (companySettings.allowedIP) {
    if (!isIpValid) { isLocationValid = false; statusMessage = "未連接公司網路"; }
    else { statusMessage = "已連接公司網路"; }
  } else if (companySettings.companyLat) {
    if (!isGpsValid) { isLocationValid = false; statusMessage = distance ? `距離公司過遠 (${distance}m)` : "定位中..."; }
    else { statusMessage = `定位有效 (距離 ${distance}m)`; }
  }

  // ★ 恢復嚴格檢查：上下班都必須符合 isLocationValid
  const canPunch = isLocationValid;

  const handlePunch = async () => {
    if (!user || isProcessing || !canPunch) return;
    setIsProcessing(true); 
    
    try {
        const type = nextAction;
        const punchTime = new Date();
        const testTimestamp = Timestamp.fromDate(punchTime);
        const logData = { 
            type, 
            timestamp: testTimestamp, 
            ip: currentIP || "unknown", 
            device: "mobile-web", 
            uid: user.uid, 
            userName: userProfile?.name || "Unknown", 
            lat: currentLocation?.lat || null, 
            lng: currentLocation?.lng || null, 
            photoURL: userProfile?.photoURL || null 
        };

      await addDoc(collection(db, "artifacts", APP_ID, "users", user.uid, "logs"), logData);
      await addDoc(collection(db, "artifacts", APP_ID, "public", "data", "company_logs"), logData);
      
      // 更新最新狀態
      await setDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", user.uid), { 
          uid: user.uid, 
          name: userProfile.name, 
          photoURL: userProfile.photoURL || null, 
          status: type, 
          lastTime: testTimestamp, 
          dateStr: formatDate(punchTime) 
      }, { merge: true });

      if (navigator.vibrate) navigator.vibrate(50);
      
    } catch (error) {
      alert("打卡失敗: " + error.message);
    } finally {
      setIsProcessing(false); 
    }
  };

  return (
    <div className="home-view flex flex-col items-center min-h-full">
      <section className="punch-scene" aria-label="今日打卡">
      <div className="punch-scene-landscape" aria-hidden="true" />
      <div className="punch-scene-ridge" aria-hidden="true" />
      <PaperCharm kind="mushrooms" className="punch-mushrooms" />
      <PaperCharm kind="maple" className="punch-maple" />
      <PaperCharm kind="lantern" className="punch-lantern" />
      <PaperCharm kind="grove" className="punch-grove" />
      <div className="punch-time text-center w-full">
        <div className="punch-digits font-mono font-bold">{formatTime(currentTime)}</div>
        <p className="punch-date">{formatDate(currentTime)}</p>
      </div>

      <div className={`status-pill mb-4 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 border transition-all duration-500 ${canPunch ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
        {canPunch ? <Navigation size={14} /> : <ShieldAlert size={14} />}
        <span>{statusMessage}</span>
      </div>

      <div className="punch-dial relative group">
        <button
          onClick={handlePunch}
          disabled={isProcessing || !canPunch}
          aria-label={isClockedIn ? "下班打卡" : "上班打卡"}
          title={isClockedIn ? "下班打卡" : "上班打卡"}
          className={`punch-button relative w-40 h-40 rounded-full shadow-xl flex flex-col items-center justify-center transition-all duration-300 transform active:scale-95 border-4 border-white ${!canPunch ? "bg-slate-300 cursor-not-allowed grayscale" : isClockedIn ? "punch-button-out" : "punch-button-in"}`}
        >
          {isProcessing ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          ) : !canPunch ? (
            <div className="flex flex-col items-center"><MapPin className="w-8 h-8 text-white mb-2 opacity-80" /><span className="text-xs font-bold text-white opacity-80">限制區域</span></div>
          ) : isClockedIn ? (
            <>
              <WorkSpinner />
              <span className="sr-only">下班</span>
            </>
          ) : (
            <>
              <LogIn className="w-10 h-10 text-white mb-1" />
              <span className="text-xl font-bold text-white tracking-widest">上班</span>
            </>
          )}
        </button>
      </div>

      <div className="punch-status text-center">
        <p className="text-slate-500 text-xs">目前狀態</p>
        <p className={`text-base font-bold ${isClockedIn ? "text-blue-600" : "text-slate-400"}`}>{isClockedIn ? "工作中" : "已休息"}</p>
      </div>
      </section>

      <div className="daily-records tech-panel w-full bg-white overflow-hidden flex flex-col min-h-[250px] flex-1">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase flex justify-between items-center">
          <span>{formatDate(currentTime).slice(5)} 紀錄</span><span className="text-[10px] text-slate-400">當日列表</span>
        </div>
        <div className="overflow-y-auto flex-1 p-0">
          {logs.map((log) => (
            <div key={log.id} className="flex justify-between items-center px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50">
              <span className={`text-sm font-bold ${log.type === "in" ? "text-green-600" : "text-orange-600"}`}>{log.type === "in" ? "上班" : "下班"}</span>
              <span className="font-mono text-slate-500 text-sm">{formatTime(log.dateObj)}</span>
            </div>
          ))}
          {logs.length === 0 && <div className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2"><Clock className="w-8 h-8 opacity-20" /> 尚無今日紀錄</div>}
        </div>
      </div>
    </div>
  );
}
